import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useUserState } from './userContext';
import { postJson, getJson } from '../services/api/backendClient';

const UserLimitStateContext = createContext(undefined);
const UserLimitDispatchContext = createContext(undefined);

export const FREE_PLAN_LIMITS = { adaptions: 3, uploads: 5, chats: 10 };

const PLAN_LIMITS = {
	free:              { adaptions: 3,        uploads: 5,        chats: 10 },
	basic:             { adaptions: Infinity,  uploads: Infinity, chats: Infinity },
	premium:           { adaptions: Infinity,  uploads: Infinity, chats: Infinity },
	vip:               { adaptions: Infinity, uploads: Infinity, chats: Infinity },
	unlimited:         { adaptions: Infinity, uploads: Infinity, chats: Infinity },
	unlimited_monthly: { adaptions: Infinity, uploads: Infinity, chats: Infinity },
	unlimited_yearly:  { adaptions: Infinity, uploads: Infinity, chats: Infinity },
};

const ACTIVE_SUBSCRIPTION_STATES = new Set(['active', 'trialing']);

const getLimitsForPlan = (planKey) => PLAN_LIMITS[planKey] || PLAN_LIMITS.free;

const createInitialState = () => ({
	countAdaptionsContent: 0,
	countContentUploaded: 0,
	countChatConversation: 0,
	currentPlanKey: 'free',
	isLoading: false,
	isLoaded: false,
	upgradePopupOpen: false,
	upgradePopupMessage: '',
});

const persistIncrement = async (field, accessToken) => {
	if (!accessToken || !field) return;
	try {
		await postJson('/api/user-limits/increment', { field }, {
			headers: { Authorization: `Bearer ${accessToken}` },
		});
	} catch {
		// Non-critical — counts are best-effort.
	}
};

export const UserLimitProvider = ({ children }) => {
	const [state, setState] = useState(createInitialState);
	const { user_id, email, is_authenticated, access_token } = useUserState();

	const refreshSubscriptionState = useCallback(async () => {
		if (!user_id || !is_authenticated || !access_token) {
			setState(createInitialState());
			return null;
		}

		setState((prev) => ({ ...prev, isLoading: true }));

		const params = new URLSearchParams();
		if (user_id) params.set('userId', user_id);
		if (email) params.set('email', email);

		try {
			const [limitsResult, subResult] = await Promise.allSettled([
				getJson('/api/user-limits', { headers: { Authorization: `Bearer ${access_token}` } }),
				getJson(`/api/payment/current-subscription?${params.toString()}`),
			]);

			const d = limitsResult.status === 'fulfilled' ? (limitsResult.value?.data || {}) : {};

			let planKey = 'free';
			if (subResult.status === 'fulfilled') {
				const sub = subResult.value?.subscription;
				const subState = String(sub?.subscriptionState || sub?.subscription_state || '').toLowerCase();
				if (ACTIVE_SUBSCRIPTION_STATES.has(subState)) {
					const key = String(sub?.planKey || sub?.plan_key || '').toLowerCase();
					if (key && PLAN_LIMITS[key]) planKey = key;
				}
			}

			setState((prev) => ({
				...prev,
				countAdaptionsContent: d.countAdaptionsContent ?? 0,
				countContentUploaded: d.countContentUploaded ?? 0,
				countChatConversation: d.countChatConversation ?? 0,
				currentPlanKey: planKey,
				isLoading: false,
				isLoaded: true,
			}));

			return planKey;
		} catch {
			setState((prev) => ({ ...prev, isLoading: false, isLoaded: true }));
			return null;
		}
	}, [user_id, email, is_authenticated, access_token]);

	useEffect(() => {
		refreshSubscriptionState();
		return undefined;
	}, [refreshSubscriptionState]);

	const incrementAdaptionsContent = useCallback(() => {
		if (!user_id) return;
		setState((prev) => ({ ...prev, countAdaptionsContent: (prev.countAdaptionsContent || 0) + 1 }));
		persistIncrement('count_adaptions_content', access_token);
	}, [user_id, access_token]);

	const incrementContentUploaded = useCallback(() => {
		if (!user_id) return;
		setState((prev) => ({ ...prev, countContentUploaded: (prev.countContentUploaded || 0) + 1 }));
		persistIncrement('count_content_uploaded', access_token);
	}, [user_id, access_token]);

	const incrementChatConversation = useCallback(() => {
		if (!user_id) return;
		setState((prev) => ({ ...prev, countChatConversation: (prev.countChatConversation || 0) + 1 }));
		persistIncrement('count_chat_conversation', access_token);
	}, [user_id, access_token]);

	const openUpgradePopup = useCallback((message) => {
		setState((prev) => ({ ...prev, upgradePopupOpen: true, upgradePopupMessage: String(message || '') }));
	}, []);

	const closeUpgradePopup = useCallback(() => {
		setState((prev) => ({ ...prev, upgradePopupOpen: false, upgradePopupMessage: '' }));
	}, []);

	const stateValue = useMemo(() => {
		const limits = getLimitsForPlan(state.currentPlanKey);
		return {
			...state,
			isAdaptionLimitReached: state.isLoaded && (state.countAdaptionsContent || 0) >= limits.adaptions,
			isUploadLimitReached: state.isLoaded && (state.countContentUploaded || 0) >= limits.uploads,
			isChatLimitReached: state.isLoaded && (state.countChatConversation || 0) >= limits.chats,
			adaptionLimit: limits.adaptions,
		};
	}, [state]);

	const dispatchValue = useMemo(() => ({
		incrementAdaptionsContent,
		incrementContentUploaded,
		incrementChatConversation,
		openUpgradePopup,
		closeUpgradePopup,
		refreshSubscriptionState,
	}), [incrementAdaptionsContent, incrementContentUploaded, incrementChatConversation, openUpgradePopup, closeUpgradePopup, refreshSubscriptionState]);

	return (
		<UserLimitStateContext.Provider value={stateValue}>
			<UserLimitDispatchContext.Provider value={dispatchValue}>
				{children}
			</UserLimitDispatchContext.Provider>
		</UserLimitStateContext.Provider>
	);
};

export const useUserLimitState = () => {
	const context = useContext(UserLimitStateContext);
	if (!context) {
		throw new Error('useUserLimitState must be used within a UserLimitProvider.');
	}
	return context;
};

export const useUserLimitDispatch = () => {
	const context = useContext(UserLimitDispatchContext);
	if (!context) {
		throw new Error('useUserLimitDispatch must be used within a UserLimitProvider.');
	}
	return context;
};

export const useUserLimitContext = () => {
	const state = useUserLimitState();
	const dispatch = useUserLimitDispatch();
	return useMemo(() => ({ ...state, ...dispatch }), [state, dispatch]);
};

export default UserLimitStateContext;
